import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  FiSave,
  FiX,
  FiPlus,
  FiTrash2,
  FiDollarSign,
  FiCalendar,
  FiFileText,
  FiAlignLeft,
  FiUploadCloud,
  FiCreditCard,
  FiRepeat,
  FiLayers,
  FiTarget,
  FiDownload,
  FiCheckCircle,
  FiClock,
  FiFilter,
  FiBriefcase,
} from 'react-icons/fi';
import Api from '../../Api';
import { Button, Input, Select } from '../../components';
import './style.css';
import { toast } from 'react-toastify';

export default function TransactionForm({
  companyId,
  initialData,
  onSuccess,
  onCancel,
  onSubmit, // Optional: if provided, overrides default API submission
  onApplyToSelected,
  companies = [],
}) {
  // --- Form State ---
  const [selectedCompany, setSelectedCompany] = useState(() => {
    if (initialData?.company_id) return String(initialData.company_id);
    if (initialData?.company?.id) return String(initialData.company.id);
    return ''; // Always default to empty for new transactions as requested
  });

  const [type, setType] = useState('revenue'); // 'revenue' or 'expense'
  const [relationType, setRelationType] = useState('customer'); // 'customer', 'supplier', 'employee', 'internal'
  const [relationId, setRelationId] = useState(''); // Customer, Supplier or Employee ID
  
  const getTodayDate = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const [competenceDate, setCompetenceDate] = useState(getTodayDate());
  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const [referenceCode, setReferenceCode] = useState('');
  const [accountId, setAccountId] = useState('');
  const [isPaid, setIsPaid] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');
  const [informNsu, setInformNsu] = useState(false);
  const [nsu, setNsu] = useState('');
  
  // Rateio State
  const [isRateioEnabled, setIsRateioEnabled] = useState(false);
  const [rateioItems, setRateioItems] = useState([]);
  
  // Single Category/CostCenter (when Rateio is disabled)
  const [categoryId, setCategoryId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [parentCategoryFilter, setParentCategoryFilter] = useState('');

  // Recurrence / Installments State
  const [isRecurrent, setIsRecurrent] = useState(false);
  
  // Recurrent Fields
  const [recurrenceFrequency, setRecurrenceFrequency] = useState('MONTHLY');
  const [recurrenceDuration, setRecurrenceDuration] = useState('12'); // '12', '24' months
  const [recurrenceDay, setRecurrenceDay] = useState('');
  const [firstDueDate, setFirstDueDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  
  // Installments Fields
  const [installmentCount, setInstallmentCount] = useState(1);
  const [installmentDueDate, setInstallmentDueDate] = useState('');
  const [installmentDistribution, setInstallmentDistribution] = useState('equal'); // 'equal' or 'divide'
  const [installments, setInstallments] = useState([]);

  // Observations & Attachments
  const [activeTab, setActiveTab] = useState('observations');
  const [observations, setObservations] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);

  // --- Data Sources ---
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const initializedRef = useRef(false);

  const clearError = (field) => {
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: false }));
    }
  };

  // Auto Create Entity State (for OFX Import)
  const [isAutoCreateEntity, setIsAutoCreateEntity] = useState(false);
  const [autoEntityName, setAutoEntityName] = useState('');

  // --- Load Data ---
  useEffect(() => {
    async function loadData() {
      if (!selectedCompany) {
        setCustomers([]);
        setSuppliers([]);
        setEmployees([]);
        setCategories([]);
        setCostCenters([]);
        setAccounts([]);
        return;
      }
      
      try {
        setLoading(true);
        const [custData, suppData, empData, catData, costData, accData] = await Promise.all([
          Api.getCustomers(selectedCompany),
          Api.getSuppliers(selectedCompany),
          Api.getEmployees(selectedCompany),
          Api.getCategories(selectedCompany),
          Api.getCostCenters(selectedCompany),
          Api.getAccounts(selectedCompany),
        ]);

        setCustomers(Array.isArray(custData) ? custData : []);
        setSuppliers(Array.isArray(suppData) ? suppData : []);
        setEmployees(Array.isArray(empData) ? empData : []);
        setCategories(Array.isArray(catData) ? catData : []);
        setCostCenters(Array.isArray(costData) ? costData : []);
        setAccounts(Array.isArray(accData) ? accData : []);
      } catch (error) {
        console.error('Error loading form data:', error);
        toast.error('Erro ao carregar dados do formulário.');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [selectedCompany]);

  useEffect(() => {
    if (!initializedRef.current && categories.length > 0 && categoryId) {
        const cat = categories.find(c => c.id === categoryId);
        if (cat) {
            setParentCategoryFilter(cat.parent_id || cat.id);
            initializedRef.current = true;
        }
    }
  }, [categories, categoryId]);

  // Sync relationType with Transaction Type (Smart Default)
  // Removed to avoid conflict with initialData loading
  // Logic moved to handleTypeChange


  // Populate form on Edit
  useEffect(() => {
    if (initialData) {
      initializedRef.current = false;
      
      // Ensure selectedCompany is set from initialData
      if (initialData.company_id) {
        setSelectedCompany(String(initialData.company_id));
      } else if (initialData.company && initialData.company.id) {
        setSelectedCompany(String(initialData.company.id));
      }

      setType(initialData.type);
      // Fix timezone issue by splitting YYYY-MM-DD string
      if (initialData.date) {
        const [y, m, d] = initialData.date.split('-');
        setCompetenceDate(`${d}/${m}/${y}`);
      } else {
        setCompetenceDate('');
      }
      // Strip existing installment numbering (e.g. "(1/3)") from description to prevent duplication
      const cleanDesc = initialData.description ? initialData.description.replace(/\s*\(\d+\/\d+\)$/, '') : '';
      setDescription(cleanDesc);
      
      setValue(initialData.value); // Will be formatted by input if passed as number? No, we need to pass formatted string or number. Let's try passing number, if Input handles it.
      // Actually Input uses mask="currency". If I pass number, it might fail.
      // Let's use formatCurrencyInput for value.
      // But formatCurrencyInput expects number? No, it expects number.
      // Let's check formatCurrencyInput implementation: val.toFixed(2)... so it expects number.
      // initialData.value from backend is string (DECIMAL).
      // So Number(initialData.value).
      
      setReferenceCode(initialData.referenceCode || initialData.reference_code || '');
      setAccountId(initialData.account_id || initialData.accountId || '');
      setIsPaid(initialData.status === 'paid' || initialData.isPaid);
      
      const pDate = initialData.payment_date || initialData.paymentDate;
      if (pDate) {
        const [y, m, d] = pDate.split('-');
        setPaymentDate(`${d}/${m}/${y}`);
      }
      setObservations(initialData.notes || initialData.observations || '');
      setPaymentMethod(initialData.payment_method || initialData.paymentMethod || '');

      // Attachments
      if (initialData.attachments && initialData.attachments.length > 0) {
        setExistingAttachments(initialData.attachments);
      } else {
        setExistingAttachments([]);
      }

      const hasInternalCustomer = initialData.customer && initialData.customer.is_internal;
      const hasInternalSupplier = initialData.supplier && initialData.supplier.is_internal;

      if (hasInternalCustomer && initialData.customer_id) {
        setRelationType('internal');
        setRelationId(`customer:${initialData.customer_id}`);
      } else if (hasInternalSupplier && initialData.supplier_id) {
        setRelationType('internal');
        setRelationId(`supplier:${initialData.supplier_id}`);
      } else if (initialData.customer_id) {
        setRelationType('customer');
        setRelationId(initialData.customer_id);
      } else if (initialData.supplier_id) {
        setRelationType('supplier');
        setRelationId(initialData.supplier_id);
      } else if (initialData.employee_id) {
        setRelationType('employee');
        setRelationId(initialData.employee_id);
      } else if (initialData.autoCreateEntity) {
        setIsAutoCreateEntity(true);
        setAutoEntityName(initialData.relationName);
        setRelationType(initialData.relationType);
        setRelationId('');
      } else if (initialData.relationId && initialData.relationType) {
        // Fallback: Use generic relation info if specific _id fields are missing
        setRelationType(initialData.relationType);
        setRelationId(initialData.relationId);
      }

      // Rateio
      if (initialData.rateios && initialData.rateios.length > 0) {
        setIsRateioEnabled(true);
        setRateioItems(initialData.rateios.map(r => ({
            categoryId: r.category_id,
            costCenterId: r.cost_center_id,
            percentage: Number(r.percentage),
            value: Number(r.value)
        })));
      } else {
        setIsRateioEnabled(false);
        setCategoryId(initialData.category_id || '');
        setCostCenterId(initialData.cost_center_id || '');
      }

      // Recurrence & Due Dates
      let formattedDueDate = '';
      if (initialData.due_date) {
        const dateStr = initialData.due_date.includes('T') ? initialData.due_date.split('T')[0] : initialData.due_date;
        const [y, m, d] = dateStr.split('-');
        formattedDueDate = `${d}/${m}/${y}`;
      }

      if (initialData.is_recurrent) {
        setIsRecurrent(true);
        setRecurrenceFrequency(initialData.recurrence_frequency);
        setRecurrenceDay(initialData.recurrence_day);
        
        if (initialData.first_due_date) {
            const [y, m, d] = initialData.first_due_date.split('-');
            setFirstDueDate(`${d}/${m}/${y}`);
        } else {
            setFirstDueDate(formattedDueDate);
        }
      } else {
        setIsRecurrent(false);
        setInstallmentDueDate(formattedDueDate);
        if (initialData.installments_count && initialData.installments_count > 1) {
          setInstallmentCount(initialData.installments_count);
        }
      }
    }
  }, [initialData]);

  function handleTypeChange(newType) {
    setType(newType);
    if (newType === 'revenue') {
      setRelationType('customer');
    } else {
      setRelationType('supplier');
    }
    setRelationId('');
  }

  // --- Helpers ---
  const parseCurrency = (val) => {
    if (!val) return 0;
    if (typeof val === 'number') return val;
    return Number(val.replace(/\D/g, '')) / 100;
  };

  const formatCurrency = (val) => {
    return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatCurrencyInput = (val) => {
    if (!val) return '';
    const v = val.toFixed(2).replace('.', ',');
    const v2 = v.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
    return `R$ ${v2}`;
  };

  const formatPercentInput = (val) => {
    if (!val) return '';
    return `${val.toFixed(2).replace('.', ',')}%`;
  };

  // --- Rateio Logic ---
  const handleAddRateioItem = () => {
    setRateioItems([
      ...rateioItems,
      { categoryId: '', costCenterId: '', percentage: 0, value: 0 },
    ]);
  };

  const handleRemoveRateioItem = (index) => {
    const newItems = [...rateioItems];
    newItems.splice(index, 1);
    setRateioItems(newItems);
  };

  const updateRateioItem = (index, field, val) => {
    const newItems = [...rateioItems];
    const totalVal = parseCurrency(value);
    
    // Clear error for this field
    clearError(`rateio_${index}_${field}`);

    if (field === 'percentage') {
      const pct = parseCurrency(val);
      newItems[index].percentage = pct;
      newItems[index].value = (totalVal * pct) / 100;
      clearError(`rateio_${index}_value`); // Clear dependent error
    } else if (field === 'value') {
      const v = parseCurrency(val);
      newItems[index].value = v;
      newItems[index].percentage = totalVal > 0 ? (v / totalVal) * 100 : 0;
      clearError(`rateio_${index}_percentage`); // Clear dependent error
    } else {
      newItems[index][field] = val;
    }
    setRateioItems(newItems);
  };

  const rateioTotals = useMemo(() => {
    const totalPct = rateioItems.reduce((acc, item) => acc + (item.percentage || 0), 0);
    const totalVal = rateioItems.reduce((acc, item) => acc + (item.value || 0), 0);
    return { totalPct, totalVal };
  }, [rateioItems]);

  // --- Installments Logic ---
  useEffect(() => {
    if (!isRecurrent && installmentCount > 0 && value && installmentDueDate) {
      generateInstallments();
    }
  }, [installmentCount, value, installmentDueDate, installmentDistribution, isRecurrent, description]);

  const generateInstallments = () => {
    const totalVal = parseCurrency(value);
    if (totalVal <= 0) return;

    const newInstallments = [];
    const baseDate = installmentDueDate; // Assuming dd/mm/yyyy
    // Simple date parsing (mock) - ideally use date-fns or similar
    const [day, month, year] = baseDate.split('/').map(Number);
    
    if (!day || !month || !year) return;

    const dateObj = new Date(year, month - 1, day);

    // Strip any existing installment suffix from current description state to avoid duplication
    const cleanBaseDescription = description ? description.replace(/\s*\(\d+\/\d+\)$/, '') : '';

    for (let i = 0; i < installmentCount; i++) {
      const itemDate = new Date(dateObj);
      itemDate.setDate(1);
      itemDate.setMonth(dateObj.getMonth() + i);
      const lastDay = new Date(itemDate.getFullYear(), itemDate.getMonth() + 1, 0).getDate();
      itemDate.setDate(Math.min(dateObj.getDate(), lastDay));
      
      let itemValue = 0;
      if (installmentDistribution === 'equal') {
        itemValue = totalVal; // Each installment has same value as total? Or divided? 
        // User prompt: "As parcelas terão o mesmo valor do agendamento" -> implied Repeat value?
        // Or "O valor do agendamento será dividido entre as parcelas" -> Divided.
        // Usually "mesmo valor" means total is multiplied, but context says "Parcelamento".
        // Let's assume:
        // Option 1: Each installment = Total Value (Replication)
        // Option 2: Total Value / Count (Division)
        
        // Wait, prompt says: "As parcelas terão o mesmo valor do agendamento" (Default)
        // vs "O valor do agendamento será dividido entre as parcelas".
        // So Option 1 is Value per installment = Input Value.
        itemValue = totalVal;
      } else {
        itemValue = totalVal / installmentCount;
      }

      newInstallments.push({
        number: i + 1,
        value: itemValue,
        dueDate: itemDate.toLocaleDateString('pt-BR'),
        description: installmentCount > 1 ? `${cleanBaseDescription} (${i + 1}/${installmentCount})` : cleanBaseDescription,
        reference: referenceCode,
      });
    }
    setInstallments(newInstallments);
  };

  const updateInstallment = (index, field, val) => {
    const newInstallments = [...installments];
    if (field === 'value') {
      newInstallments[index].value = parseCurrency(val);
    } else {
      newInstallments[index][field] = val;
    }
    setInstallments(newInstallments);
  };

  // --- Attachments Logic ---
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setAttachments((prev) => [...prev, ...files]);
    }
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingAttachment = async (attachmentId) => {
    try {
      if (!window.confirm('Deseja excluir este anexo permanentemente?')) return;
      
      await Api.deleteTransactionAttachment(selectedCompany, initialData.id, attachmentId);
      
      setExistingAttachments(prev => prev.filter(a => a.id !== attachmentId));
      toast.success('Anexo excluído!');
    } catch (error) {
      console.error(error);
      toast.error('Erro ao excluir anexo.');
    }
  };

  // --- Submission ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    // Validação de Campos Obrigatórios
    if (!selectedCompany) newErrors.company_id = true;
    if (!relationType) newErrors.relationType = true;
    if (!relationId && !isAutoCreateEntity) newErrors.relationId = true;
    if (!competenceDate) newErrors.competenceDate = true;
    if (!description) newErrors.description = true;
    if (!value) newErrors.value = true;
    if (!accountId) newErrors.accountId = true;

    // Validação de Rateio
    if (isRateioEnabled) {
      if (rateioItems.length === 0) {
        toast.error('Adicione pelo menos um item ao rateio');
        return;
      }
      // Valida itens do rateio
      rateioItems.forEach((item, index) => {
        if (!item.categoryId) newErrors[`rateio_${index}_categoryId`] = true;
        if (!item.costCenterId) newErrors[`rateio_${index}_costCenterId`] = true;
        if (item.percentage <= 0 && item.value <= 0) {
            newErrors[`rateio_${index}_value`] = true;
            newErrors[`rateio_${index}_percentage`] = true;
        }
      });

      if (Math.abs(rateioTotals.totalPct - 100) > 0.1) {
        toast.error('A soma das porcentagens do rateio deve ser 100%');
        return;
      }
    } else {
      if (!categoryId) newErrors.categoryId = true;
      if (!costCenterId) newErrors.costCenterId = true;
    }

    // Regras comuns (Vencimento e Forma de Pagamento)
    if (!paymentMethod) newErrors.paymentMethod = true;

    // Validação de Recorrência / Parcelamento
    if (isRecurrent) {
        if (!recurrenceFrequency) newErrors.recurrenceFrequency = true;
        if (!recurrenceDuration) newErrors.recurrenceDuration = true;
        if (!recurrenceDay) newErrors.recurrenceDay = true;
        if (!firstDueDate) newErrors.firstDueDate = true;
    } else {
        if (!installmentCount) newErrors.installmentCount = true;
        if (!installmentDueDate) newErrors.installmentDueDate = true;
    }

    if (isPaid && !paymentDate) {
        newErrors.paymentDate = true;
    }

    if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        toast.error('Preencha os campos obrigatórios destacados em vermelho (*)');
        return;
    }

    setErrors({});

    try {
      setSubmitting(true);

      // Parse dates to YYYY-MM-DD
      const parseDate = (dateStr) => {
        if (!dateStr) return null;
        const [day, month, year] = dateStr.split('/');
        return `${year}-${month}-${day}`;
      };

      const formattedDate = parseDate(competenceDate);
      const formattedFirstDueDate = isRecurrent ? parseDate(firstDueDate) : null;

      let customerId = null;
      let supplierId = null;

      if (!isAutoCreateEntity) {
        if (relationType === 'customer') {
          customerId = relationId;
        } else if (relationType === 'supplier') {
          supplierId = relationId;
        } else if (relationType === 'internal' && typeof relationId === 'string') {
          const [kind, id] = relationId.split(':');
          if (kind === 'customer') customerId = id;
          if (kind === 'supplier') supplierId = id;
        }
      }

      const payload = {
        company_id: selectedCompany,
        type,
        autoCreateEntity: isAutoCreateEntity,
        relationName: isAutoCreateEntity ? autoEntityName : null,
        customer_id: customerId,
        supplier_id: supplierId,
        employee_id: relationType === 'employee' ? relationId : null,
        category_id: !isRateioEnabled ? categoryId : null,
        competenceDate: formattedDate,
        description,
        value: parseCurrency(value),
        referenceCode,
        account_id: accountId || null,
        status: isPaid ? 'paid' : 'pending',
        paymentDate: isPaid ? parseDate(paymentDate) : null,
        paymentMethod,
        informNsu,
        nsu,
        isRateioEnabled,
        rateio: isRateioEnabled ? rateioItems : [{ categoryId, costCenterId, percentage: 100, value: parseCurrency(value) }],
        isRecurrent,
        recurrence: isRecurrent ? {
          frequency: recurrenceFrequency,
          duration: recurrenceDuration,
          day: recurrenceDay,
          firstDueDate: formattedFirstDueDate,
          paymentMethod,
        } : null,
        installments: !isRecurrent ? installments.map(i => ({
          ...i,
          dueDate: parseDate(i.dueDate)
        })) : null,
        observations,
      };

      // Check for attachments
      if (attachments.length > 0) {
        const formData = new FormData();
        // Append all payload fields
        Object.keys(payload).forEach(key => {
            if (payload[key] === null || payload[key] === undefined) return;
            
            if (typeof payload[key] === 'object') {
                formData.append(key, JSON.stringify(payload[key]));
            } else {
                formData.append(key, payload[key]);
            }
        });
        
        // Append files
        attachments.forEach(file => {
            formData.append('files', file);
        });

        if (onSubmit) {
            await onSubmit(formData);
        } else if (initialData) {
            await Api.updateTransaction(selectedCompany, initialData.id, formData);
            toast.success('Lançamento atualizado com sucesso!');
        } else {
            await Api.createTransaction(selectedCompany, formData);
            toast.success('Lançamento criado com sucesso!');
        }
      } else {
        // Send JSON as before
        if (onSubmit) {
            await onSubmit(payload);
        } else if (initialData) {
            await Api.updateTransaction(selectedCompany, initialData.id, payload);
            toast.success('Lançamento atualizado com sucesso!');
        } else {
            await Api.createTransaction(selectedCompany, payload);
            toast.success('Lançamento criado com sucesso!');
        }
      }

      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar lançamento.');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Options ---
  const recurrenceOptions = [
    { value: 'MONTHLY', label: 'MENSAL' },
    { value: 'QUARTERLY', label: 'TRIMESTRAL' },
    { value: 'SEMIANNUAL', label: 'SEMESTRAL' },
    { value: 'ANNUAL', label: 'ANUAL' },
  ];

  const recurrenceDurationOptions = [
    { value: '12', label: '1 Ano' },
    { value: '24', label: '2 Anos' },
    { value: '36', label: '3 Anos' },
    { value: '48', label: '4 Anos' },
    { value: '60', label: '5 Anos' },
    { value: '72', label: '6 Anos' },
    { value: '84', label: '7 Anos' },
    { value: '96', label: '8 Anos' },
    { value: '108', label: '9 Anos' },
    { value: '120', label: '10 Anos' },
  ];

  const paymentMethods = [
    { value: 'BOLETO', label: 'Boleto' },
    { value: 'PIX', label: 'PIX' },
    { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
    { value: 'DEBIT_CARD', label: 'Cartão de Débito' },
    { value: 'TRANSFER', label: 'Transferência' },
    { value: 'CASH', label: 'Dinheiro' },
  ];

  const relationOptions = useMemo(() => {
    if (relationType === 'customer') {
      return customers
        .filter(c => !c.is_internal)
        .map(c => ({ value: c.id, label: c.name }));
    }
    if (relationType === 'supplier') {
      return suppliers
        .filter(s => !s.is_internal)
        .map(s => ({ value: s.id, label: s.name }));
    }
    if (relationType === 'employee') {
      return employees.map(e => ({ value: e.id, label: e.name }));
    }
    if (relationType === 'internal') {
      const internalCustomers = customers
        .filter(c => c.is_internal)
        .map(c => ({ value: `customer:${c.id}`, label: c.name }));
      const internalSuppliers = suppliers
        .filter(s => s.is_internal)
        .map(s => ({ value: `supplier:${s.id}`, label: s.name }));
      return [...internalCustomers, ...internalSuppliers];
    }
    return [];
  }, [relationType, customers, suppliers, employees]);

  const categoryOptions = useMemo(() => {
    const filterType = type === 'revenue' ? 'Receita' : 'Despesa';
    let filtered = categories.filter(c => c.type === filterType);

    if (parentCategoryFilter) {
       filtered = filtered.filter(c => c.parent_id == parentCategoryFilter || c.id == parentCategoryFilter);
    }
    
    return filtered.map(c => ({ value: c.id, label: c.name }));
  }, [categories, type, parentCategoryFilter]);

  const parentFilterOptions = useMemo(() => {
    const filterType = type === 'revenue' ? 'Receita' : 'Despesa';
    return categories
        .filter(c => c.type === filterType && !c.parent_id)
        .map(c => ({ value: c.id, label: c.name }));
  }, [categories, type]);

  const costCenterOptions = costCenters.map(cc => ({ value: cc.id, label: `${cc.code || ''} ${cc.name}` }));
  const accountOptions = accounts.map(a => ({ value: a.id, label: a.name }));

  return (
    <form onSubmit={handleSubmit} className="transaction-form-component">
      <div className="form-body">
        <div className="transaction-form-section">
          <div className="transaction-form-title">
            <FiBriefcase /> Empresa
          </div>
          <Select
            id="company"
            label="Empresa *"
            value={selectedCompany}
            disabled={!!companyId && companies.length === 1}
            onChange={(e) => {
                setSelectedCompany(e.target.value);
                clearError('company_id');
                setRelationId('');
                setCategoryId('');
                setCostCenterId('');
                setAccountId('');
                setRateioItems([]);
            }}
            options={[{ value: '', label: 'SELECIONAR EMPRESA...' }, ...companies]}
            searchable
            error={errors.company_id}
          />
        </div>

        {/* SEÇÃO: Informações do Lançamento */}
        <div className="transaction-form-section">
        <div className="transaction-form-title">
          <FiFileText /> Informações do Lançamento
        </div>

        <div className="transaction-type-selector">
          <Select
            label="Tipo de Lançamento"
            options={[
              { value: 'revenue', label: 'Receita' },
              { value: 'expense', label: 'Despesa' }
            ]}
            value={type}
            onChange={(e) => handleTypeChange(e.target.value)}
            searchable={false}
          />
        </div>

        <div className="field-group-row">
          <div style={{ flex: '0 0 200px' }}>
            <Select
              label="Tipo de Vínculo *"
              options={[
                { value: 'customer', label: 'Cliente' },
                { value: 'supplier', label: 'Fornecedor' },
                { value: 'employee', label: 'Funcionário' },
                { value: 'internal', label: 'Interno' }
              ]}
              value={relationType}
              onChange={(e) => {
                setRelationType(e.target.value);
                clearError('relationType');
                setRelationId('');
              }}
              searchable
              className={errors.relationType ? 'has-error' : ''}
              error={errors.relationType}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flex: 1, flexDirection: 'column', alignItems: 'stretch' }}>
            <Select
              id="relation"
              label={
                (relationType === 'customer' && 'Cliente *') ||
                (relationType === 'supplier' && 'Fornecedor *') ||
                (relationType === 'employee' && 'Funcionário *') ||
                'Interno *'
              }
              options={relationOptions}
              value={relationId}
              onChange={(e) => {
                  setRelationId(e.target.value);
                  clearError('relationId');
                  if (e.target.value) setIsAutoCreateEntity(false);
              }}
              searchable
              disabled={isAutoCreateEntity}
              placeholder={isAutoCreateEntity ? `Novo: ${autoEntityName}` : "SELECIONE..."}
              className={`flex-1 ${errors.relationId ? 'has-error' : ''}`}
              error={errors.relationId}
            />
            {isAutoCreateEntity && (
               <div className="info-message" style={{ marginTop: '4px', color: 'var(--primary-color)', fontSize: '0.85rem', display: 'flex', alignItems: 'center' }}>
                  <FiCheckCircle style={{ marginRight: 4 }} />
                  Novo {relationType === 'customer' ? 'Cliente' : 'Fornecedor'} será criado: <strong>{autoEntityName}</strong>
                  <button 
                    type="button" 
                    onClick={() => setIsAutoCreateEntity(false)} 
                    style={{ marginLeft: 8, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}
                  >
                    (Alterar)
                  </button>
               </div>
            )}
          </div>
        </div>

        <div className="field-group-row">
          <Input
            id="competenceDate"
            label="Data de Competência *"
            mask="date"
            value={competenceDate}
            onChange={(e) => {
                setCompetenceDate(e.target.value);
                clearError('competenceDate');
            }}
            placeholder="dd/mm/aaaa"
            icon={FiCalendar}
            className={errors.competenceDate ? 'has-error' : ''}
            error={errors.competenceDate}
          />
          <Input
            id="description"
            label="Descrição *"
            value={description}
            onChange={(e) => {
                setDescription(e.target.value);
                clearError('description');
            }}
            icon={FiAlignLeft}
            className={errors.description ? 'has-error' : ''}
            error={errors.description}
          />
          <Input
            id="value"
            label="Valor *"
            mask="currency"
            value={value}
            onChange={(e) => {
                setValue(e.target.value);
                clearError('value');
            }}
            icon={FiDollarSign}
            className={errors.value ? 'has-error' : ''}
            error={errors.value}
          />
        </div>

        <div className="field-group-row">
          <Select
            id="accountId"
            label="Conta Bancária *"
            options={accountOptions}
            value={accountId}
            onChange={(e) => {
                setAccountId(e.target.value);
                clearError('accountId');
            }}
            searchable
            placeholder="Selecione a conta..."
            icon={FiCreditCard}
            className={errors.accountId ? 'has-error' : ''}
            error={errors.accountId}
          />
          <Input
            id="referenceCode"
            label="Código de referência"
            value={referenceCode}
            onChange={(e) => setReferenceCode(e.target.value)}
          />
        </div>

        {/* Sistema de Rateio */}
        <div 
          className={`toggle-container feature-toggle-container ${isRateioEnabled ? 'active' : ''}`} 
          onClick={() => setIsRateioEnabled(!isRateioEnabled)}
        >
          <div className="toggle-track">
            <div className="toggle-thumb" />
          </div>
          <div className="toggle-content">
            <span className="toggle-title">Habilitar Rateio</span>
            <span className="toggle-description">Dividir o valor em múltiplas categorias ou centros de custo</span>
          </div>
        </div>

        {isRateioEnabled ? (
          <div className="rateio-container">
            <div className="rateio-header">
              <span className="toggle-label">Informar detalhes</span>
              <Button type="button" onClick={handleAddRateioItem} variant="outline" size="sm">
                <FiPlus /> Adicionar
              </Button>
            </div>
            
            {rateioItems.map((item, index) => (
              <div key={index} className="rateio-row">
                <Select
                  options={categoryOptions}
                  value={item.categoryId}
                  onChange={(e) => updateRateioItem(index, 'categoryId', e.target.value)}
                  placeholder="Categoria *"
                  searchable
                  icon={FiLayers}
                  className={errors[`rateio_${index}_categoryId`] ? 'has-error' : ''}
                  error={errors[`rateio_${index}_categoryId`]}
                />
                <Select
                  options={costCenterOptions}
                  value={item.costCenterId}
                  onChange={(e) => updateRateioItem(index, 'costCenterId', e.target.value)}
                  placeholder="Centro de custo *"
                  searchable
                  icon={FiTarget}
                  className={errors[`rateio_${index}_costCenterId`] ? 'has-error' : ''}
                  error={errors[`rateio_${index}_costCenterId`]}
                />
                <Input
                  value={item.percentage ? formatPercentInput(item.percentage) : ''}
                  onChange={(e) => updateRateioItem(index, 'percentage', e.target.value)}
                  placeholder="%"
                  mask="percent"
                  className={errors[`rateio_${index}_percentage`] ? 'has-error' : ''}
                  error={errors[`rateio_${index}_percentage`]}
                />
                <Input
                  mask="currency"
                  value={item.value ? formatCurrencyInput(item.value) : ''}
                  onChange={(e) => updateRateioItem(index, 'value', e.target.value)}
                  placeholder="R$"
                  className={errors[`rateio_${index}_value`] ? 'has-error' : ''}
                  error={errors[`rateio_${index}_value`]}
                />
                <Button 
                  type="button" 
                  variant="ghost" 
                  className="manager-icon-button-danger"
                  onClick={() => handleRemoveRateioItem(index)}
                >
                  <FiTrash2 />
                </Button>
              </div>
            ))}

            <div className="rateio-footer">
              <span>
                Restante do rateio: {(100 - rateioTotals.totalPct).toFixed(2)}% / {formatCurrency(parseCurrency(value) - rateioTotals.totalVal)}
              </span>
              <span>Total rateado: {formatCurrency(rateioTotals.totalVal)}</span>
            </div>
          </div>
        ) : (
          <>
          <div className="field-group-row">
            <Select
                label="Filtrar Grupo"
                options={[{ value: '', label: 'Todos' }, ...parentFilterOptions]}
                value={parentCategoryFilter}
                onChange={(e) => setParentCategoryFilter(e.target.value)}
                searchable
                placeholder="Todos"
                icon={FiFilter}
            />
            <Select
              id="category"
              label="Categoria *"
              options={categoryOptions}
              value={categoryId}
              onChange={(e) => {
                  setCategoryId(e.target.value);
                  clearError('categoryId');
              }}
              searchable
              placeholder="SELECIONE..."
              icon={FiLayers}
              className={errors.categoryId ? 'has-error' : ''}
              error={errors.categoryId}
            />
          </div>
          <div className="field-group-row">
            <Select
              id="costCenter"
              label="Centro de custo *"
              options={costCenterOptions}
              value={costCenterId}
              onChange={(e) => {
                  setCostCenterId(e.target.value);
                  clearError('costCenterId');
              }}
              searchable
              placeholder="SELECIONE..."
              icon={FiTarget}
              className={errors.costCenterId ? 'has-error' : ''}
              error={errors.costCenterId}
            />
          </div>
          </>
        )}
      </div>

      {/* SEÇÃO: Condição de Recebimento/Pagamento */}
      <div className="transaction-form-section">
        <div className="transaction-form-title">
          <FiCreditCard /> Condição de Recebimento/Pagamento
        </div>

        <div 
          className={`toggle-container feature-toggle-container ${isRecurrent ? 'active' : ''} ${initialData?.id ? 'disabled' : ''}`} 
          onClick={() => !initialData?.id && setIsRecurrent(!isRecurrent)}
          style={initialData?.id ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
        >
          <div className="toggle-track">
            <div className="toggle-thumb" />
          </div>
          <div className="toggle-content">
            <span className="toggle-title">Repetir lançamento?</span>
            <span className="toggle-description">Configurar recorrência automática deste lançamento (Mensal, Semanal...)</span>
          </div>
        </div>

        {isRecurrent ? (
          <>
            <div className="field-group-row">
              <Select
                label="Repetição*"
                options={recurrenceOptions}
                value={recurrenceFrequency}
                onChange={(e) => {
                    setRecurrenceFrequency(e.target.value);
                    clearError('recurrenceFrequency');
                }}
                searchable
                placeholder="SELECIONE..."
                icon={FiRepeat}
                className={errors.recurrenceFrequency ? 'has-error' : ''}
                error={errors.recurrenceFrequency}
              />
              <Select
                label="Duração*"
                options={recurrenceDurationOptions}
                value={recurrenceDuration}
                onChange={(e) => {
                    setRecurrenceDuration(e.target.value);
                    clearError('recurrenceDuration');
                }}
                searchable
                placeholder="SELECIONE..."
                icon={FiClock}
                className={errors.recurrenceDuration ? 'has-error' : ''}
                error={errors.recurrenceDuration}
              />
              <Select
                label="Cobrar sempre no *"
                options={Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: `${i + 1}º DIA DO MÊS` }))}
                value={recurrenceDay}
                onChange={(e) => {
                    setRecurrenceDay(e.target.value);
                    clearError('recurrenceDay');
                }}
                searchable
                placeholder="Selecione..."
                icon={FiCalendar}
                className={errors.recurrenceDay ? 'has-error' : ''}
                error={errors.recurrenceDay}
              />
            </div>
            <div className="field-group-row">
              <Input
                label="1º vencimento *"
                mask="date"
                value={firstDueDate}
                onChange={(e) => {
                    setFirstDueDate(e.target.value);
                    clearError('firstDueDate');
                }}
                icon={FiCalendar}
                className={errors.firstDueDate ? 'has-error' : ''}
                error={errors.firstDueDate}
              />
              <Select
                label="Forma de pagamento *"
                options={paymentMethods}
                value={paymentMethod}
                onChange={(e) => {
                    setPaymentMethod(e.target.value);
                    clearError('paymentMethod');
                }}
                searchable
                placeholder="SELECIONE..."
                icon={FiCreditCard}
                className={errors.paymentMethod ? 'has-error' : ''}
                error={errors.paymentMethod}
              />
            </div>
          </>
        ) : (
          <>
            <div className="field-group-row">
              <Select
                label="Parcelamento *"
                options={Array.from({ length: 60 }, (_, i) => ({ value: i + 1, label: `X${i + 1}` }))}
                value={installmentCount}
                onChange={(e) => {
                    setInstallmentCount(Number(e.target.value));
                    clearError('installmentCount');
                }}
                searchable
                disabled={!!initialData?.id}
                className={errors.installmentCount ? 'has-error' : ''}
                error={errors.installmentCount}
              />
              <Input
                label="Vencimento *"
                mask="date"
                value={installmentDueDate}
                onChange={(e) => {
                    setInstallmentDueDate(e.target.value);
                    clearError('installmentDueDate');
                }}
                icon={FiCalendar}
                className={errors.installmentDueDate ? 'has-error' : ''}
                error={errors.installmentDueDate}
              />
              <Select
                label="Forma de pagamento *"
                options={paymentMethods}
                value={paymentMethod}
                onChange={(e) => {
                    setPaymentMethod(e.target.value);
                    clearError('paymentMethod');
                }}
                searchable
                placeholder="SELECIONE..."
                icon={FiCreditCard}
                className={errors.paymentMethod ? 'has-error' : ''}
                error={errors.paymentMethod}
              />
            </div>

            {installmentCount > 1 && (
              <div className="transaction-type-selector">
                <label className="radio-label">
                  <input 
                    type="radio" 
                    name="dist" 
                    value="equal" 
                    checked={installmentDistribution === 'equal'} 
                    onChange={() => setInstallmentDistribution('equal')} 
                  />
                  As parcelas terão o mesmo valor do agendamento
                </label>
                <label className="radio-label">
                  <input 
                    type="radio" 
                    name="dist" 
                    value="divide" 
                    checked={installmentDistribution === 'divide'} 
                    onChange={() => setInstallmentDistribution('divide')} 
                  />
                  O valor do agendamento será dividido entre as parcelas
                </label>
              </div>
            )}

            {installments.length > 0 && (
              <table className="installments-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Valor (R$)</th>
                    <th>Vencimento</th>
                    <th>Descrição</th>
                    <th>Referência</th>
                  </tr>
                </thead>
                <tbody>
                  {installments.map((inst, idx) => (
                    <tr key={idx}>
                      <td>{inst.number}</td>
                      <td>
                        <Input 
                          mask="currency"
                          value={formatCurrencyInput(inst.value)}
                          onChange={(e) => updateInstallment(idx, 'value', e.target.value)}
                          className="table-input"
                        />
                      </td>
                      <td>
                        <Input 
                          mask="date"
                          value={inst.dueDate}
                          onChange={(e) => updateInstallment(idx, 'dueDate', e.target.value)}
                          className="table-input"
                        />
                      </td>
                      <td>
                        <Input 
                          value={inst.description}
                          onChange={(e) => updateInstallment(idx, 'description', e.target.value)}
                          className="table-input"
                        />
                      </td>
                      <td>
                        <Input 
                          value={inst.reference}
                          onChange={(e) => updateInstallment(idx, 'reference', e.target.value)}
                          className="table-input"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'right', fontWeight: 'bold', padding: '1rem' }}>
                      Total: {formatCurrency(installments.reduce((acc, i) => acc + i.value, 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </>
        )}

        <div 
          className={`toggle-container feature-toggle-container ${isPaid ? 'active' : ''}`} 
          onClick={() => {
            const newState = !isPaid;
            setIsPaid(newState);
            if (newState && !paymentDate) setPaymentDate(getTodayDate());
          }}
        >
          <div className="toggle-track">
            <div className="toggle-thumb" />
          </div>
          <div className="toggle-content">
            <span className="toggle-title">Informar {type === 'revenue' ? 'recebimento' : 'pagamento'}</span>
            <span className="toggle-description">
              {isPaid 
                ? `O lançamento será marcado como ${type === 'revenue' ? 'recebido' : 'pago'}`
                : `O lançamento ficará pendente de ${type === 'revenue' ? 'recebimento' : 'pagamento'}`
              }
            </span>
          </div>
        </div>

        {isPaid && (
          <div className="field-group-row" style={{ marginTop: '1rem' }}>
             <Input
               label={type === 'revenue' ? 'Data do Recebimento' : 'Data do Pagamento'}
               mask="date"
               value={paymentDate}
               onChange={(e) => setPaymentDate(e.target.value)}
               icon={FiCalendar}
               className={errors.paymentDate ? 'has-error' : ''}
             />
          </div>
        )}
      </div>

      {/* SEÇÃO: Observações e Anexos */}
      <div className="transaction-form-section">
        <div className="transaction-form-title">
          <FiLayers /> Observações e Anexos
        </div>

        <div className="field-group-row" style={{ flexDirection: 'column', gap: '1rem' }}>
          <div style={{ width: '100%' }}>
            <label className="field-label" style={{ marginBottom: '0.5rem', display: 'block', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Observações
            </label>
            <textarea
              className="field-input"
              rows="3"
              placeholder="Descreva observações relevantes sobre esse lançamento..."
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              style={{ width: '100%', resize: 'vertical', minHeight: '80px' }}
            />
          </div>

          <div style={{ width: '100%' }}>
            <label className="field-label" style={{ marginBottom: '0.5rem', display: 'block', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Anexos
            </label>
            
            <div className="attachments-wrapper">
               <label htmlFor="file-upload" className="attachment-button">
                <FiUploadCloud /> Adicionar Anexo
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </label>
              
              {(attachments.length > 0 || existingAttachments.length > 0) && (
                <div className="attachments-list">
                  {/* Existing Attachments */}
                  {existingAttachments.map((file) => (
                    <div key={`existing-${file.id}`} className="attachment-item">
                      <span className="attachment-name">{file.file_name}</span>
                      <div className="attachment-actions">
                        <a 
                          href={`data:${file.file_type};base64,${file.file_data}`} 
                          download={file.file_name}
                          className="download-attachment-btn"
                          title="Baixar"
                        >
                          <FiDownload size={14} />
                        </a>
                        <button 
                          type="button" 
                          className="remove-attachment-btn"
                          onClick={() => handleRemoveExistingAttachment(file.id)}
                          title="Excluir"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* New Attachments */}
                  {attachments.map((file, index) => (
                    <div key={`new-${index}`} className="attachment-item">
                      <span className="attachment-name">{file.name}</span>
                      <button 
                        type="button" 
                        className="remove-attachment-btn"
                        onClick={() => removeAttachment(index)}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>

      <div className="form-actions">
        <Button
          type="button"
          className="cancel-button"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={submitting || loading}>
          {submitting ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
